import { Backdrop, Box, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../theme/theme";

// Fullscreen overlay shown while a SWMM (.inp) file is being generated and
// uploaded. Theme: a small pipe network at the top funnels into a document
// that "writes" itself line by line (section headers + data rows), with a
// pulsing download arrow at the bottom. Below it rotating status messages.

const MESSAGES = [
  "Reconstructing network...",
  "Writing junctions...",
  "Writing conduits...",
  "Writing cross-sections...",
  "Uploading file...",
  "Preparing download...",
];

// Document page geometry (viewBox 0 0 300 300).
const PAGE = { x: 90, y: 70, w: 120, h: 170 };
const LINE_X = PAGE.x + 14;

// Lines inside the document: section headers (accent) + data rows (grey).
const LINES = [
  { y: 90, w: 62, header: true }, // [TITLE]
  { y: 102, w: 88 },
  { y: 120, w: 78, header: true }, // [JUNCTIONS]
  { y: 132, w: 92 },
  { y: 142, w: 80 },
  { y: 152, w: 90 },
  { y: 170, w: 74, header: true }, // [CONDUITS]
  { y: 182, w: 92 },
  { y: 192, w: 84 },
  { y: 210, w: 82, header: true }, // [XSECTIONS]
  { y: 222, w: 90 },
];
const WRITE_STAGGER = 0.2; // s between consecutive lines
const WRITE_CYCLE =
  LINES.length * WRITE_STAGGER + 1.4; // full "writing" loop length

// small network glyph feeding into the page
const GLYPH_NODES = [
  { x: 130, y: 30 },
  { x: 170, y: 26 },
  { x: 150, y: 46 },
  { x: 150, y: 60 },
];
const GLYPH_EDGES = [
  [0, 2],
  [1, 2],
  [2, 3],
];

export const SwmmExportOverlay = ({ active }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [messageIndex, setMessageIndex] = useState(0);
  useEffect(() => {
    if (!active) return;
    setMessageIndex(0);
    const interval = setInterval(
      () => setMessageIndex((i) => (i + 1) % MESSAGES.length),
      2200
    );
    return () => clearInterval(interval);
  }, [active]);

  const accent = "hsl(210, 85%, 50%)";
  const dataColor = theme.palette.mode === "dark" ? "#8a97a6" : "#9aa5b1";
  const pageFill = theme.palette.mode === "dark" ? "#2a2f36" : "#ffffff";
  const pageStroke = theme.palette.mode === "dark" ? "#4a525c" : "#d5dbe1";
  const outfallColor = "hsl(150, 70%, 45%)";

  return (
    <Backdrop
      open={Boolean(active)}
      sx={{
        zIndex: 99999999,
        bgcolor: colors.contrast[200] + "80",
        backdropFilter: "blur(10px)",
      }}
    >
      {Boolean(active) && (
        <Box
          className="flex flex-col items-center gap-5"
          sx={{
            "& .swmm-line": {
              transformBox: "fill-box",
              transformOrigin: "left center",
            },
            "& .swmm-glyph-edge": {
              stroke: accent,
              strokeWidth: 2,
              strokeLinecap: "round",
            },
            "& .swmm-glyph-node": { fill: accent },
            "& .swmm-feed": {
              stroke: accent,
              strokeWidth: 2,
              strokeDasharray: "3 5",
              animation: "swmmFeed 0.7s linear infinite",
            },
            "& .swmm-download": {
              fill: outfallColor,
              transformBox: "fill-box",
              transformOrigin: "center",
              animation: "swmmDownload 1.6s ease-in-out infinite",
            },
            "@keyframes swmmWrite": {
              "0%": { transform: "scaleX(0)", opacity: 0 },
              "8%": { opacity: 1 },
              "35%": { transform: "scaleX(1)", opacity: 1 },
              "90%": { transform: "scaleX(1)", opacity: 1 },
              "100%": { transform: "scaleX(1)", opacity: 0 },
            },
            "@keyframes swmmFeed": {
              to: { strokeDashoffset: -8 },
            },
            "@keyframes swmmDownload": {
              "0%, 100%": { transform: "translateY(0)", opacity: 0.7 },
              "50%": { transform: "translateY(4px)", opacity: 1 },
            },
            "@keyframes swmmFadeIn": {
              from: { opacity: 0, transform: "translateY(6px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <svg
            width="300"
            height="300"
            viewBox="0 0 300 300"
            style={{ overflow: "visible", maxWidth: "80vw" }}
          >
            {/* network glyph feeding into the document */}
            {GLYPH_EDGES.map(([a, b], i) => (
              <line
                key={`ge-${i}`}
                className="swmm-glyph-edge"
                x1={GLYPH_NODES[a].x}
                y1={GLYPH_NODES[a].y}
                x2={GLYPH_NODES[b].x}
                y2={GLYPH_NODES[b].y}
              />
            ))}
            {GLYPH_NODES.map((n, i) => (
              <circle
                key={`gn-${i}`}
                className="swmm-glyph-node"
                cx={n.x}
                cy={n.y}
                r={3.5}
              />
            ))}
            {/* dashed feed from the glyph into the top of the page */}
            <line
              className="swmm-feed"
              x1={150}
              y1={62}
              x2={150}
              y2={PAGE.y}
            />

            {/* document page with a folded corner */}
            <path
              d={`M${PAGE.x},${PAGE.y}
                 h${PAGE.w - 18}
                 l18,18
                 v${PAGE.h - 18}
                 h${-PAGE.w}
                 z`}
              fill={pageFill}
              stroke={pageStroke}
              strokeWidth={2}
            />
            <path
              d={`M${PAGE.x + PAGE.w - 18},${PAGE.y}
                 l18,18
                 h${-18} z`}
              fill={pageStroke}
              opacity={0.6}
            />

            {/* lines "typing" into the document, looping */}
            {LINES.map((line, i) => (
              <rect
                key={`l-${i}`}
                className="swmm-line"
                x={LINE_X}
                y={line.y}
                width={line.w}
                height={line.header ? 5 : 3.5}
                rx={1.5}
                fill={line.header ? accent : dataColor}
                style={{
                  animation: `swmmWrite ${WRITE_CYCLE}s ease-out ${
                    i * WRITE_STAGGER
                  }s infinite`,
                }}
              />
            ))}

            {/* download arrow below the page */}
            <g className="swmm-download">
              <path d="M150,250 v18" stroke={outfallColor} strokeWidth={3} />
              <path
                d="M143,262 l7,7 l7,-7 z"
                fill={outfallColor}
                stroke="none"
              />
            </g>
          </svg>

          <Typography variant="h3">{t("Exporting SWMM file")}</Typography>
          <Typography
            key={messageIndex}
            variant="h5"
            sx={{ opacity: 0.8, animation: "swmmFadeIn 0.5s ease-out" }}
          >
            {t(MESSAGES[messageIndex])}
          </Typography>
        </Box>
      )}
    </Backdrop>
  );
};

export default SwmmExportOverlay;
